import { useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { cx, focusRing } from './cx';
import { useEdgeFade } from './useEdgeFade';

export interface FilterChipsProps<V extends string> {
  /** [value, label] pairs, in order. */
  options: Array<readonly [V, ReactNode]>;
  value: V;
  onChange: (value: V) => void;
  /** What the filter is for, announced to a screen reader: "Filter by kind". */
  label: string;
  className?: string;
}

/**
 * A row of filter chips: one choice at a time, which narrows the list
 * below it.
 *
 * The chips used to wrap onto two rows on a phone and mark the chosen one
 * as a solid ink block, which is neither the green the direction reserves
 * for the selected state nor anything else in the product. They now stay on
 * one row that scrolls sideways if it has to, and the chosen chip is the
 * selected state the rest of the app uses: a green edge on the green tint,
 * in the deep green at 600.
 *
 * Tab semantics, as the hand-built rows had: arrow keys move the choice,
 * and only the chosen chip is in the tab order. The edge with more chips
 * behind it fades, so a phone shows that the row goes on.
 */
export function FilterChips<V extends string>({ options, value, onChange, label, className }: FilterChipsProps<V>) {
  const listRef = useRef<HTMLDivElement>(null);
  const fade = useEdgeFade(listRef, [options.length]);
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const i = options.findIndex(([v]) => v === value);
      let next = -1;
      if (e.key === 'ArrowRight') next = (i + 1) % options.length;
      else if (e.key === 'ArrowLeft') next = (i - 1 + options.length) % options.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = options.length - 1;
      if (next < 0) return;
      e.preventDefault();
      onChange(options[next][0]);
      listRef.current?.querySelectorAll<HTMLElement>('[role=tab]')[next]?.focus();
    },
    [options, value, onChange],
  );
  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      style={fade}
      // 4px of padding all round, pulled back by the margin: a scroller
      // clips whatever leaves its padding box, and the focus ring sits 2px
      // out and is 2px wide. At 2px it lost its top and bottom edges, so a
      // keyboard user saw two green bars either side of the chosen chip.
      className={cx('-mx-1 -my-1 flex flex-nowrap gap-1.5 overflow-x-auto p-1', className)}
    >
      {options.map(([v, text]) => {
        const selected = v === value;
        return (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(v)}
            className={cx(
              'min-h-tap shrink-0 whitespace-nowrap rounded-ct-sm border px-3 py-1.5 text-sm transition-colors duration-state ease-ct',
              focusRing,
              selected
                ? 'border-forest bg-forest-whisper font-semibold text-forest-deep'
                : 'border-line bg-paper-card text-ink-2 hover:border-forest-soft hover:text-ink active:bg-forest-faint',
            )}
          >
            {text}
          </button>
        );
      })}
    </div>
  );
}
